import { Dimmer, Form, Header, Label, List, Loader } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { MODES_REGLEMENTS, MODES_REGLEMENTS_LIBELLE } from '@/constants';

import { ModeReglementContext } from '@/contexts';
import type {
  BonCAFType,
  BonCCASType,
  CarteBancaireType,
  CesuType,
  ChequeType,
  ChequeVacancesType,
  DataReglementType,
  FactureType,
  NumeraireType,
  PrelevementType,
  TicketType,
  VirementType,
} from '@/types';
import BonCAF from './BonCAF';
import BonCCAS from './BonCCAS';
import CarteBancaire from './CarteBancaire';
import Cesu from './Cesu';
import Cheque from './Cheque';
import ChequeVacances from './ChequeVacances';
import Numeraire from './Numeraire';
import PaiementEnLigne from './PaiementEnLigne';
import Prelevement from './Prelevement';
import Tickets from './Tickets';
import Virement from './Virement';

export interface ReglementsInlineProps {
  loading?: boolean;
  reglements: DataReglementType[];
  factures?: FactureType[];
  onSelect?: (id: number | string) => void;
  selectedItems?: (number | string)[];
}

const ReglementsInline = ({
  loading,
  reglements,
  factures,
  onSelect,
  selectedItems,
}: ReglementsInlineProps) => {
  const { modesReglements } = React.useContext(ModeReglementContext);

  const { compteBancairePreleve, titulaireComptePreleve } = _.head(factures) || {};

  if (loading) {
    return (
      <List as={Form}>
        <List.Item style={{ height: 60 }}>
          <Dimmer active inverted>
            <Loader>Chargement des règlements</Loader>
          </Dimmer>
        </List.Item>
      </List>
    );
  }
  return (
    <>
      {!_.isNil(onSelect) && _.size(reglements) > 1 && (
        <Header as="h3" style={{ marginTop: '1em' }}>
          <Header.Subheader>
            <i>Cliquer sur un règlement pour voir sa répartition par facture et produit :</i>
          </Header.Subheader>
        </Header>
      )}
      <List
        as={Form}
        divided
        relaxed="very"
        style={{ marginBottom: 0 }}
        {...(onSelect && _.size(reglements) > 1 && { selection: true })}
      >
        {_.map(reglements, (reglement, index) => {
          const { identifiant, identifiantModeReglement } = reglement;

          // Repli sur le mode porté par le règlement : CBI et PRI ne sont pas toujours actifs sur
          // la régie, alors qu'un paiement en ligne redescendu de MFO les utilise quand même.
          const paiement =
            _.find(modesReglements, { identifiant: identifiantModeReglement }) ??
            reglement.modeReglement;

          const informationsInline = _.omit(reglement, ['identifiant', 'identifiantModeReglement']);

          const selected = _.includes(selectedItems, identifiant);

          return (
            <List.Item
              data-testid={`reglement@${reglement.identifiant}`}
              key={`reglement@${reglement.identifiant}`}
              style={{ display: 'flex', alignItems: 'center' }}
              {...(onSelect && _.size(reglements) > 1 && { onClick: () => onSelect(identifiant) })}
            >
              <Label
                {...(onSelect && _.size(reglements) > 1 && { as: 'a' })}
                size="small"
                style={{
                  ...((selected || _.isNil(onSelect) || _.size(reglements) === 1) && {
                    backgroundColor: 'var(--current-color, var(--accent, var(--primary)))',
                    color: 'white',
                  }),
                  marginRight: '2em',
                }}
              >
                {_.size(reglements) > 1 ? `Règlement ${index + 1}` : 'Règlement'}
              </Label>
              {paiement?.code === MODES_REGLEMENTS.CHEQUE && (
                <Cheque {...(informationsInline as unknown as ChequeType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.NUMERAIRE && (
                <Numeraire {...(informationsInline as unknown as NumeraireType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.CARTE_BANCAIRE && (
                <CarteBancaire {...(informationsInline as unknown as CarteBancaireType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.CESU && (
                <Cesu {...(informationsInline as unknown as CesuType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.CHEQUE_VAC && (
                <ChequeVacances {...(informationsInline as unknown as ChequeVacancesType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.BON_CCAS && (
                <BonCCAS {...(informationsInline as unknown as BonCCASType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.BON_CAF && (
                <BonCAF {...(informationsInline as unknown as BonCAFType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.TICKET && (
                <Tickets {...(informationsInline as unknown as TicketType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.VIREMENT && (
                <Virement {...(informationsInline as unknown as VirementType)} />
              )}
              {paiement?.code === MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET && (
                <PaiementEnLigne
                  libelle={MODES_REGLEMENTS_LIBELLE.CARTE_BANCAIRE_INTERNET}
                  {...(informationsInline as unknown as CarteBancaireType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.PRELEVEMENT_INTERNET && (
                <PaiementEnLigne
                  libelle={MODES_REGLEMENTS_LIBELLE.PRELEVEMENT_INTERNET}
                  {...(informationsInline as unknown as CarteBancaireType)}
                />
              )}
              {paiement?.code === MODES_REGLEMENTS.PRELEVEMENT && (
                <Prelevement
                  {...(informationsInline as unknown as PrelevementType)}
                  compteBancairePreleve={compteBancairePreleve}
                  titulaireComptePreleve={titulaireComptePreleve}
                />
              )}
              <div style={{ width: 40 }} />
            </List.Item>
          );
        })}
      </List>
    </>
  );
};

export default ReglementsInline;
